import { create } from 'zustand';

import { clearOverlay, setOverlay } from '../engine/host.js';
import { docIO, SAVE_DELAY_MS } from '../lib/doc-io.js';
import {
  addLayer,
  addPattern,
  addTrack,
  addVoiceLine,
  applySolo,
  beatsPerBar,
  deleteClip,
  deleteInstrument,
  deleteLayer,
  deleteNotes,
  deletePattern,
  deleteTrack,
  deleteVoiceLine,
  duplicateClip,
  duplicateLayer,
  duplicateNotes,
  duplicatePattern,
  duplicateTrack,
  duplicateVoiceLine,
  getAtPath,
  moveClip,
  moveNotes,
  nudgeLayer,
  parseSound,
  referencedFiles,
  serializeSound,
  setAtPath,
  setKind,
  clipSpan,
  timeSignatureOf,
  updateLayer,
  updateTrack,
  updateVoiceLine,
  type DocPath,
  type SoundDoc,
  type SoundKind,
} from '../lib/sound-doc.js';
import { soundPlayer } from '../lib/sound-player.js';
import { invalidateSoundFile, renderSound, type RenderedAudio, type SoundAnalysis } from '../lib/sound-render.js';
import { logEditor } from './console.js';
import { useWorkspace } from './workspace.js';

/**
 * The open sound document (`*.sound.json`) in the Sound tab.
 *
 * Edits replace the whole (immutable) document, go on the undo stack, are
 * saved after a short debounce and re-rendered after a slightly longer one.
 * Rendering happens in the browser (prism_wasm `SoundRenderer` in a worker)
 * with the backend `prism sound render` as fallback; the last good render
 * and its analysis are kept while a new one runs or when it fails.
 */

const HISTORY_LIMIT = 200;
export const RENDER_DELAY_MS = 650;

export type SaveState = 'saved' | 'pending' | 'saving' | 'error';
export type SoundView = 'timeline' | 'pianoRoll' | 'steps';
export type SoundSide = 'inspector' | 'analysis' | 'presets' | 'downloads';

/** What the inspector shows. Indices address `layers`, `tracks`, `tracks[i].clips`, `voice.lines`. */
export type SoundSelection =
  | { kind: 'doc' }
  | { kind: 'layer'; index: number }
  | { kind: 'track'; index: number }
  | { kind: 'clip'; track: number; clip: number }
  | { kind: 'pattern'; name: string }
  | { kind: 'instrument'; name: string }
  | { kind: 'voice' }
  | { kind: 'voiceLine'; index: number }
  | { kind: 'master' }
  | { kind: 'return'; name: string };

export interface RenderState {
  status: 'idle' | 'queued' | 'rendering' | 'done' | 'error';
  stage: string;
  /** 0..1, or null when indeterminate. */
  progress: number | null;
  error: string | null;
  source: 'wasm' | 'server' | null;
  warnings: string[];
  /** ms the last render took. */
  took: number | null;
}

export interface SoundState {
  path: string | null;
  doc: SoundDoc | null;
  loading: boolean;
  error: string | null;
  saveState: SaveState;
  /** True while the document has edits not yet written to disk. */
  dirty: boolean;
  past: SoundDoc[];
  future: SoundDoc[];

  selection: SoundSelection | null;
  /** The selection as an edit path (`layers[2]`, `tracks.Piano`) — what AI context chips show. */
  selected: string | null;
  /** Pattern open in the piano roll / step sequencer. */
  activePattern: string | null;
  /** Selected note indices in `activePattern`. */
  notes: number[];
  view: SoundView;
  /** Editor-only solo keys (`layer:<i>`, `track:<i>`, `voice`); applied when rendering. */
  solo: string[];
  variant: string | null;
  autoRender: boolean;
  /** Right panel tab. */
  side: SoundSide;

  render: RenderState;
  audio: RenderedAudio | null;
  analysis: SoundAnalysis | null;
  images: { spectrogram: string | null; waveform: string | null };

  open: (path: string) => Promise<void>;
  /** Write a new document (blank or from a preset) and open it. */
  create: (path: string, doc: SoundDoc) => Promise<void>;
  close: () => void;
  /** Replace the whole document (inspector edits). */
  update: (doc: SoundDoc, selection?: SoundSelection | null) => void;
  /** Set one value by JSON path (`undefined` removes it). */
  setPath: (path: DocPath, value: unknown) => void;
  getPath: (path: DocPath) => unknown;
  /** JSON path of the current selection inside the document (null for doc/none). */
  selectionPath: () => DocPath | null;
  select: (selection: SoundSelection | null) => void;
  selectNotes: (indices: number[]) => void;
  openPattern: (name: string | null, view?: SoundView) => void;
  setView: (view: SoundView) => void;
  setSide: (side: SoundSide) => void;
  setKind: (kind: SoundKind) => void;
  rename: (name: string) => void;
  toggleMute: (key: string) => void;
  toggleSolo: (key: string) => void;
  deleteSelection: () => void;
  duplicateSelection: () => void;
  /** Arrow-key nudge: dx in grid steps (time), dy in steps (pitch/track order). */
  nudge: (dx: number, dy: number, fine?: boolean) => void;
  addLayer: () => void;
  addTrack: () => void;
  addPattern: () => void;
  addVoiceLine: () => void;
  setVariant: (variant: string | null) => void;
  setAutoRender: (on: boolean) => void;
  renderNow: (options?: { serverOnly?: boolean }) => Promise<void>;
  undo: () => void;
  redo: () => void;
  externalChange: (path: string) => Promise<void>;
  /** Any project file changed (referenced samples re-render the open sound). */
  fileChanged: (path: string) => void;
  flush: () => Promise<void>;
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
let renderTimer: ReturnType<typeof setTimeout> | null = null;
let renderGeneration = 0;
let rendering: Promise<void> | null = null;
let renderAgain = false;
const lastDisk = new Map<string, string>();

function projectId(): number | null {
  return useWorkspace.getState().projectId;
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

const IDLE_RENDER: RenderState = { status: 'idle', stage: '', progress: null, error: null, source: null, warnings: [], took: null };

/** Note grid (beats) used by keyboard nudges; the piano roll keeps it in sync. */
export const soundGrid = { beats: 0.25, seconds: 0.01 };

function revoke(images: { spectrogram: string | null; waveform: string | null }): void {
  for (const url of [images.spectrogram, images.waveform]) if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
}

/** Selection still valid in `doc`? */
function validSelection(doc: SoundDoc, sel: SoundSelection | null): SoundSelection | null {
  if (!sel) return null;
  switch (sel.kind) {
    case 'layer':
      return doc.layers?.[sel.index] ? sel : null;
    case 'track':
      return doc.tracks?.[sel.index] ? sel : null;
    case 'clip':
      return doc.tracks?.[sel.track]?.clips?.[sel.clip] ? sel : null;
    case 'pattern':
      return doc.patterns?.[sel.name] ? sel : null;
    case 'instrument':
      return doc.instruments?.[sel.name] || (sel.name === '' && doc.instrument) ? sel : null;
    case 'voiceLine':
      return doc.voice?.lines?.[sel.index] ? sel : null;
    case 'voice':
      return doc.voice ? sel : null;
    case 'return':
      return doc.master?.returns?.[sel.name] ? sel : null;
    default:
      return sel;
  }
}

export function selectionPathOf(sel: SoundSelection | null): DocPath | null {
  if (!sel) return null;
  switch (sel.kind) {
    case 'layer':
      return ['layers', sel.index];
    case 'track':
      return ['tracks', sel.index];
    case 'clip':
      return ['tracks', sel.track, 'clips', sel.clip];
    case 'pattern':
      return ['patterns', sel.name];
    case 'instrument':
      return sel.name === '' ? ['instrument'] : ['instruments', sel.name];
    case 'voice':
      return ['voice'];
    case 'voiceLine':
      return ['voice', 'lines', sel.index];
    case 'master':
      return ['master'];
    case 'return':
      return ['master', 'returns', sel.name];
    default:
      return null;
  }
}

/**
 * The selection as a prism_sound_edit path for AI requests: `layers[2]`,
 * `tracks.Piano.clips[0]`, `patterns.verse`, `instruments.lead`,
 * `voice.lines[1]`, `master.returns.reverb`.
 */
export function selectionLabel(doc: SoundDoc | null, sel: SoundSelection | null): string | null {
  if (!doc || !sel) return null;
  const trackRef = (i: number) => {
    const name = doc.tracks?.[i]?.name;
    const unique = name && (doc.tracks ?? []).filter((t) => t.name === name).length === 1 && /^[\w-]+$/.test(name);
    return unique ? `tracks.${name}` : `tracks[${i}]`;
  };
  switch (sel.kind) {
    case 'layer':
      return `layers[${sel.index}]`;
    case 'track':
      return trackRef(sel.index);
    case 'clip':
      return `${trackRef(sel.track)}.clips[${sel.clip}]`;
    case 'pattern':
      return `patterns.${sel.name}`;
    case 'instrument':
      return sel.name ? `instruments.${sel.name}` : 'instrument';
    case 'voice':
      return 'voice';
    case 'voiceLine':
      return `voice.lines[${sel.index}]`;
    case 'master':
      return 'master';
    case 'return':
      return `master.returns.${sel.name}`;
    default:
      return null;
  }
}

/** Default center view for a document. */
function defaultView(doc: SoundDoc): SoundView {
  if (doc.kind === 'instrument' && doc.patterns && Object.keys(doc.patterns).length) return 'pianoRoll';
  return 'timeline';
}

function firstPattern(doc: SoundDoc): string | null {
  return Object.keys(doc.patterns ?? {})[0] ?? null;
}

export const useSound = create<SoundState>((set, get) => {
  function commit(doc: SoundDoc, options: { history?: boolean; save?: boolean; selection?: SoundSelection | null; notes?: number[] } = {}): void {
    const { history = true, save = true } = options;
    const state = get();
    const prev = state.doc;
    const selection = validSelection(doc, options.selection !== undefined ? options.selection : state.selection);
    const activePattern = state.activePattern && doc.patterns?.[state.activePattern] ? state.activePattern : firstPattern(doc);
    const noteCount = activePattern ? (doc.patterns?.[activePattern]?.notes?.length ?? 0) : 0;
    const notes = (options.notes ?? (activePattern === state.activePattern ? state.notes : [])).filter((i) => i >= 0 && i < noteCount);
    set({
      doc,
      selection,
      activePattern,
      notes,
      past: history && prev ? [...state.past, prev].slice(-HISTORY_LIMIT) : state.past,
      future: history ? [] : state.future,
    });
    if (state.path) setOverlay(state.path, serializeSound(doc));
    if (save) scheduleSave();
    scheduleRender();
  }

  function scheduleSave(): void {
    if (saveTimer) clearTimeout(saveTimer);
    set({ saveState: 'pending', dirty: true });
    saveTimer = setTimeout(() => {
      saveTimer = null;
      void get().flush();
    }, SAVE_DELAY_MS);
  }

  function scheduleRender(delay = RENDER_DELAY_MS): void {
    if (!get().autoRender) return;
    if (renderTimer) clearTimeout(renderTimer);
    set((s) => ({ render: { ...s.render, status: s.render.status === 'rendering' ? 'rendering' : 'queued' } }));
    renderTimer = setTimeout(() => {
      renderTimer = null;
      void get().renderNow();
    }, delay);
  }

  /** Edit the active selection's target with a pure helper. */
  function edit(fn: (doc: SoundDoc) => { doc: SoundDoc; selection?: SoundSelection | null; notes?: number[] } | SoundDoc): void {
    const doc = get().doc;
    if (!doc) return;
    const out = fn(doc);
    const result = 'format' in out ? { doc: out } : out;
    if (result.doc === doc) return;
    commit(result.doc, { ...(result.selection !== undefined ? { selection: result.selection } : {}), ...(result.notes ? { notes: result.notes } : {}) });
  }

  function loaded(path: string, doc: SoundDoc, text: string): void {
    lastDisk.set(path, text);
    setOverlay(path, text);
    revoke(get().images);
    soundPlayer.setAudio(null);
    set({
      path,
      doc,
      loading: false,
      error: null,
      saveState: 'saved',
      dirty: false,
      past: [],
      future: [],
      selection: null,
      activePattern: firstPattern(doc),
      notes: [],
      view: defaultView(doc),
      solo: [],
      variant: null,
      render: IDLE_RENDER,
      audio: null,
      analysis: null,
      images: { spectrogram: null, waveform: null },
    });
    if (get().autoRender) scheduleRender(50);
  }

  return {
    path: null,
    doc: null,
    loading: false,
    error: null,
    saveState: 'saved',
    dirty: false,
    past: [],
    future: [],
    selection: null,
    selected: null,
    activePattern: null,
    notes: [],
    view: 'timeline',
    solo: [],
    variant: null,
    autoRender: true,
    side: 'inspector',
    render: IDLE_RENDER,
    audio: null,
    analysis: null,
    images: { spectrogram: null, waveform: null },

    open: async (path) => {
      const pid = projectId();
      if (!pid) {
        set({ path });
        return;
      }
      await get().flush();
      set({ path, loading: true, error: null });
      try {
        const text = await docIO().read(pid, path);
        if (get().path !== path) return;
        loaded(path, parseSound(text), text);
      } catch (error) {
        if (get().path !== path) return;
        set({ doc: null, loading: false, error: `${path}: ${errorText(error)}` });
      }
    },

    create: async (path, doc) => {
      const pid = projectId();
      if (!pid) return;
      await get().flush();
      const text = serializeSound(doc);
      try {
        await docIO().write(pid, path, text);
      } catch (error) {
        logEditor('error', `Could not create ${path}: ${errorText(error)}`);
        return;
      }
      loaded(path, doc, text);
    },

    close: () => {
      if (saveTimer) clearTimeout(saveTimer);
      if (renderTimer) clearTimeout(renderTimer);
      saveTimer = renderTimer = null;
      renderGeneration++;
      const { path, images } = get();
      if (path) clearOverlay(path);
      revoke(images);
      soundPlayer.setAudio(null);
      set({
        path: null,
        doc: null,
        error: null,
        saveState: 'saved',
        dirty: false,
        past: [],
        future: [],
        selection: null,
        activePattern: null,
        notes: [],
        solo: [],
        render: IDLE_RENDER,
        audio: null,
        analysis: null,
        images: { spectrogram: null, waveform: null },
      });
    },

    update: (doc, selection) => commit(doc, selection === undefined ? {} : { selection }),

    setPath: (path, value) => edit((doc) => setAtPath(doc, path, value)),
    getPath: (path) => getAtPath(get().doc, path),
    selectionPath: () => selectionPathOf(get().selection),

    select: (selection) => {
      const doc = get().doc;
      set({ selection: doc ? validSelection(doc, selection) : null });
      if (selection?.kind === 'pattern') get().openPattern(selection.name);
    },

    selectNotes: (notes) => set({ notes: [...new Set(notes)].sort((a, b) => a - b) }),

    openPattern: (name, view) => {
      const doc = get().doc;
      if (!doc || (name !== null && !doc.patterns?.[name])) return;
      const pattern = name ? doc.patterns?.[name] : null;
      const auto: SoundView = pattern?.drums && !pattern.notes?.length ? 'steps' : 'pianoRoll';
      set({ activePattern: name, notes: name === get().activePattern ? get().notes : [], ...(name ? { view: view ?? auto } : {}) });
    },

    setView: (view) => set({ view }),
    setSide: (side) => set({ side }),

    setKind: (kind) => {
      const doc = get().doc;
      if (!doc || doc.kind === kind) return;
      commit(setKind(doc, kind));
      set({ view: kind === 'song' || kind === 'sfx' || kind === 'voice' ? 'timeline' : get().view });
    },

    rename: (name) => edit((doc) => (name && name !== doc.name ? { ...doc, name } : doc)),

    toggleMute: (key) => {
      const [kind, raw] = key.split(':');
      const index = Number(raw);
      edit((doc) => {
        if (kind === 'layer' && doc.layers?.[index]) return updateLayer(doc, index, { mute: doc.layers[index]!.mute ? undefined : true });
        if (kind === 'track' && doc.tracks?.[index]) return updateTrack(doc, index, { mute: doc.tracks[index]!.mute ? undefined : true });
        return doc;
      });
    },

    toggleSolo: (key) => {
      const solo = get().solo;
      set({ solo: solo.includes(key) ? solo.filter((k) => k !== key) : [...solo, key] });
      scheduleRender(80);
    },

    deleteSelection: () => {
      const { selection, activePattern, notes, view } = get();
      if ((view === 'pianoRoll' || view === 'steps') && activePattern && notes.length) {
        edit((doc) => ({ doc: deleteNotes(doc, activePattern, notes), notes: [] }));
        return;
      }
      if (!selection) return;
      edit((doc) => {
        switch (selection.kind) {
          case 'layer':
            return { doc: deleteLayer(doc, selection.index), selection: null };
          case 'track':
            return { doc: deleteTrack(doc, selection.index), selection: null };
          case 'clip':
            return { doc: deleteClip(doc, selection.track, selection.clip), selection: { kind: 'track', index: selection.track } };
          case 'pattern':
            return { doc: deletePattern(doc, selection.name), selection: null };
          case 'instrument':
            return selection.name ? { doc: deleteInstrument(doc, selection.name), selection: null } : doc;
          case 'voiceLine':
            return { doc: deleteVoiceLine(doc, selection.index), selection: null };
          case 'return': {
            const returns = { ...(doc.master?.returns ?? {}) };
            delete returns[selection.name];
            return { doc: { ...doc, master: { ...doc.master, returns } }, selection: null };
          }
          default:
            return doc;
        }
      });
    },

    duplicateSelection: () => {
      const { selection, activePattern, notes, view } = get();
      if ((view === 'pianoRoll' || view === 'steps') && activePattern && notes.length) {
        edit((doc) => {
          const r = duplicateNotes(doc, activePattern, notes);
          return { doc: r.doc, notes: r.indices };
        });
        return;
      }
      if (!selection) return;
      edit((doc) => {
        switch (selection.kind) {
          case 'layer': {
            const r = duplicateLayer(doc, selection.index);
            return { doc: r.doc, selection: { kind: 'layer', index: r.index } };
          }
          case 'track': {
            const r = duplicateTrack(doc, selection.index);
            return { doc: r.doc, selection: { kind: 'track', index: r.index } };
          }
          case 'clip': {
            const r = duplicateClip(doc, selection.track, selection.clip);
            return { doc: r.doc, selection: { kind: 'clip', track: selection.track, clip: r.index } };
          }
          case 'pattern': {
            const r = duplicatePattern(doc, selection.name);
            return { doc: r.doc, selection: { kind: 'pattern', name: r.name } };
          }
          case 'voiceLine': {
            const r = duplicateVoiceLine(doc, selection.index);
            return { doc: r.doc, selection: { kind: 'voiceLine', index: r.index } };
          }
          default:
            return doc;
        }
      });
    },

    nudge: (dx, dy, fine = false) => {
      const { selection, activePattern, notes, view } = get();
      if (view === 'pianoRoll' && activePattern && notes.length) {
        const grid = fine ? soundGrid.beats / 4 : soundGrid.beats;
        edit((doc) => {
          const r = moveNotes(doc, activePattern, notes, dx * grid, dy);
          return { doc: r.doc, notes: r.indices };
        });
        return;
      }
      if (!selection) return;
      edit((doc) => {
        if (selection.kind === 'layer') return nudgeLayer(doc, selection.index, dx * (fine ? soundGrid.seconds : soundGrid.seconds * 10));
        if (selection.kind === 'voiceLine') {
          const line = doc.voice?.lines?.[selection.index];
          if (!line) return doc;
          const start = Math.max(0, Math.round(((line.start ?? 0) + dx * (fine ? 0.01 : 0.1)) * 1000) / 1000);
          return updateVoiceLine(doc, selection.index, { start });
        }
        if (selection.kind === 'clip') {
          const clip = doc.tracks?.[selection.track]?.clips?.[selection.clip];
          if (!clip) return doc;
          const ts = timeSignatureOf(doc);
          const step = fine ? 1 : beatsPerBar(ts);
          const span = clipSpan(doc, clip);
          const toTrack = Math.max(0, Math.min((doc.tracks?.length ?? 1) - 1, selection.track + dy));
          const r = moveClip(doc, selection.track, selection.clip, span.start + dx * step, toTrack);
          return { doc: r.doc, selection: { kind: 'clip', track: r.track, clip: r.index } };
        }
        return doc;
      });
    },

    addLayer: () =>
      edit((doc) => {
        const r = addLayer(doc, {
          name: 'layer',
          start: 0,
          gain: -6,
          source: { type: 'osc', wave: 'sine', freq: 440 },
          amp: { env: [[0, 0], [0.005, 1], [0.4, 0, 'exp']] },
        });
        return { doc: r.doc, selection: { kind: 'layer', index: r.index } };
      }),

    addTrack: () =>
      edit((doc) => {
        let next = doc;
        let instrument = Object.keys(doc.instruments ?? {})[0];
        if (!instrument) {
          next = { ...next, instruments: { piano: { type: 'soundfont', bank: 0, program: 0 } } };
          instrument = 'piano';
        }
        const r = addTrack(next, { name: 'Track', instrument, clips: [] });
        return { doc: r.doc, selection: { kind: 'track', index: r.index } };
      }),

    addPattern: () => {
      edit((doc) => {
        const r = addPattern(doc, 'pattern');
        return { doc: r.doc, selection: { kind: 'pattern', name: r.name } };
      });
      const sel = get().selection;
      if (sel?.kind === 'pattern') get().openPattern(sel.name, 'pianoRoll');
    },

    addVoiceLine: () =>
      edit((doc) => {
        const r = addVoiceLine(doc, { text: 'New line.' });
        return { doc: r.doc, selection: { kind: 'voiceLine', index: r.index } };
      }),

    setVariant: (variant) => {
      set({ variant });
      scheduleRender(50);
    },

    setAutoRender: (autoRender) => {
      set({ autoRender });
      if (autoRender) scheduleRender(50);
    },

    renderNow: async (options = {}) => {
      if (renderTimer) clearTimeout(renderTimer);
      renderTimer = null;
      if (rendering) {
        renderAgain = true;
        return rendering;
      }
      const run = async () => {
        const { path, doc, solo, variant } = get();
        const pid = projectId();
        if (!path || !doc || !pid) return;
        const generation = ++renderGeneration;
        const started = performance.now();
        set((s) => ({ render: { ...s.render, status: 'rendering', stage: 'Rendering', progress: null, error: null } }));
        try {
          const result = await renderSound({
            projectId: pid,
            path,
            doc: applySolo(doc, solo),
            variant,
            serverOnly: options.serverOnly,
            save: () => get().flush(),
            onProgress: (stage, progress) => {
              if (generation === renderGeneration) set((s) => ({ render: { ...s.render, stage, progress } }));
            },
          });
          if (generation !== renderGeneration || get().path !== path) {
            for (const url of [result.spectrogram, result.waveform]) if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
            return;
          }
          revoke(get().images);
          soundPlayer.setAudio(result.audio);
          set({
            audio: result.audio,
            analysis: result.analysis ?? get().analysis,
            images: { spectrogram: result.spectrogram, waveform: result.waveform },
            render: {
              status: 'done',
              stage: '',
              progress: 1,
              error: null,
              source: result.source,
              warnings: result.warnings,
              took: Math.round(performance.now() - started),
            },
          });
        } catch (error) {
          if (generation !== renderGeneration) return;
          // Keep the last good audio and analysis.
          set((s) => ({ render: { ...s.render, status: 'error', stage: '', progress: null, error: errorText(error) } }));
        }
      };
      rendering = (async () => {
        try {
          do {
            renderAgain = false;
            await run();
          } while (renderAgain);
        } finally {
          rendering = null;
        }
      })();
      return rendering;
    },

    undo: () => {
      const { past, doc, future } = get();
      const prev = past.at(-1);
      if (!prev || !doc) return;
      set({ past: past.slice(0, -1), future: [doc, ...future].slice(0, HISTORY_LIMIT) });
      commit(prev, { history: false });
    },

    redo: () => {
      const { past, doc, future } = get();
      const next = future[0];
      if (!next || !doc) return;
      set({ past: [...past, doc].slice(-HISTORY_LIMIT), future: future.slice(1) });
      commit(next, { history: false });
    },

    externalChange: async (path) => {
      const pid = projectId();
      if (!pid || path !== get().path) return;
      let text: string;
      try {
        text = await docIO().read(pid, path);
      } catch {
        return;
      }
      if (get().path !== path) return;
      const doc = get().doc;
      if (text === lastDisk.get(path) || (doc && text === serializeSound(doc))) return;
      lastDisk.set(path, text);
      let parsed: SoundDoc;
      try {
        parsed = parseSound(text);
      } catch (error) {
        set({ error: `${path}: ${errorText(error)}` });
        return;
      }
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = null;
      set({ error: null, saveState: 'saved', dirty: false });
      commit(parsed, { save: false });
    },

    fileChanged: (path) => {
      invalidateSoundFile(path);
      const { path: open, doc } = get();
      if (!open || !doc) return;
      if (path === open) {
        void get().externalChange(path);
        return;
      }
      if (referencedFiles(doc).includes(path)) scheduleRender(200);
    },

    flush: async () => {
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = null;
      const { path, doc, saveState } = get();
      const pid = projectId();
      if (!path || !doc || !pid || saveState === 'saved') return;
      const text = serializeSound(doc);
      if (text === lastDisk.get(path)) {
        set({ saveState: 'saved', dirty: false });
        return;
      }
      set({ saveState: 'saving' });
      try {
        lastDisk.set(path, text);
        await docIO().write(pid, path, text);
        if (get().path === path && get().saveState === 'saving') set({ saveState: 'saved', dirty: false });
      } catch (error) {
        lastDisk.delete(path);
        set({ saveState: 'error' });
        logEditor('error', `Could not save ${path}: ${errorText(error)}`);
      }
    },
  };
});

/* Convenience edits used by the views (kept here so they share undo/render). */

export function soundEdit(fn: (doc: SoundDoc) => SoundDoc, selection?: SoundSelection | null, notes?: number[]): void {
  const s = useSound.getState();
  if (!s.doc) return;
  const next = fn(s.doc);
  if (next === s.doc) return;
  s.update(next, selection);
  if (notes) s.selectNotes(notes);
}

// Keep the derived `selected` label in sync with the selection and document.
useSound.subscribe((state, prev) => {
  if (state.selection === prev.selection && state.doc === prev.doc) return;
  const label = selectionLabel(state.doc, state.selection);
  if (label !== state.selected) useSound.setState({ selected: label });
});
